import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Bars2Icon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import Button from "../../../shared/Button";
import Badge from "../../../shared/Badge";
import { formatDate } from "../../../utils";
import { useUpdateManifestV2Mutation } from "../../../redux/services/manifests/manifestsV2Api";
import { getLegJobPath, isManifestLegsEditable } from "../utils";
import ManifestV2LegPicker from "./ManifestV2LegPicker";

const DND_TYPE = "manifest-v2-leg-row";

function DragHandle() {
  return <Bars2Icon className="h-4 w-4 text-gray-400" aria-hidden="true" />;
}

function LegRow({ leg, index, canEdit, canReorder, moveRow, onRemove }) {
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: DND_TYPE,
    hover(item) {
      if (!ref.current || item.index === index) return;
      moveRow(item.index, index);
      item.index = index;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: DND_TYPE,
    item: { index },
    canDrag: canReorder,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  drag(drop(ref));

  const jobPath = getLegJobPath(leg);

  return (
    <tr
      ref={ref}
      className={isDragging ? "opacity-50" : undefined}
      data-testid={`manifest-v2-leg-row-${leg._id}`}
    >
      <td className="px-3 py-2">
        {canReorder ? <DragHandle /> : index + 1}
      </td>
      <td className="px-3 py-2">
        {jobPath ? (
          <Link className="font-medium text-blue-600 hover:underline" to={jobPath}>
            {leg.loadNumber || leg._id}
          </Link>
        ) : (
          leg.loadNumber || leg._id
        )}
      </td>
      <td className="px-3 py-2">{leg.status || "—"}</td>
      <td className="px-3 py-2">{leg.shipping?.bolNumber || "—"}</td>
      <td className="px-3 py-2">{leg.shipping?.commodity || "—"}</td>
      <td className="px-3 py-2">
        {leg.shipping?.isHazmat ? <Badge color="amber" label="Hazmat" /> : "—"}
      </td>
      <td className="px-3 py-2">{formatDate(leg.appointmentDate, null, true)}</td>
      <td className="px-3 py-2 text-right">
        {jobPath ? (
          <Link className="text-sm text-blue-600 hover:underline" to={jobPath}>
            Edit leg
          </Link>
        ) : null}
        {canEdit ? (
          <Button
            type="alternative"
            size="xs"
            className="ml-2"
            onClick={() => onRemove(leg._id)}
          >
            Remove
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * Drag-reorder legs table with add/remove for editable manifests.
 *
 * @param {object} props
 * @param {object} props.manifest
 * @param {boolean} [props.canEdit]
 * @param {string} props.parentCompany
 */
const ManifestV2LegsTable = ({ manifest, canEdit = false, parentCompany }) => {
  const [updateManifestV2, { isLoading }] = useUpdateManifestV2Mutation();
  const [showPicker, setShowPicker] = useState(false);
  const [pickerSelection, setPickerSelection] = useState([]);

  const legs = useMemo(
    () => (Array.isArray(manifest.legs) ? manifest.legs : []),
    [manifest.legs]
  );
  const [orderedLegs, setOrderedLegs] = useState(legs);
  const orderedLegsRef = useRef(legs);
  const lastSavedOrderRef = useRef(legs.map((leg) => String(leg._id)));

  const editable = canEdit && isManifestLegsEditable(manifest.status);
  const canReorder = editable;

  useEffect(() => {
    setOrderedLegs(legs);
    orderedLegsRef.current = legs;
    lastSavedOrderRef.current = legs.map((leg) => String(leg._id));
  }, [manifest._id, legs]);

  const persistSortOrder = useCallback(
    async (nextLegIds) => {
      if (!manifest?._id || !nextLegIds.length) return;

      const prevIds = lastSavedOrderRef.current || [];
      const isSame =
        prevIds.length === nextLegIds.length &&
        prevIds.every((id, idx) => id === nextLegIds[idx]);
      if (isSame) return;

      try {
        await updateManifestV2({
          id: manifest._id,
          body: { manifestSortOrder: nextLegIds },
        }).unwrap();
        lastSavedOrderRef.current = nextLegIds;
        toast.success("Leg order updated");
      } catch (error) {
        toast.error(error?.data?.message || "Failed to update leg order");
        setOrderedLegs((prev) => {
          const map = new Map(prev.map((leg) => [String(leg._id), leg]));
          return (lastSavedOrderRef.current || [])
            .map((id) => map.get(String(id)))
            .filter(Boolean);
        });
      }
    },
    [manifest?._id, updateManifestV2]
  );

  const moveRow = useCallback((fromIndex, toIndex) => {
    setOrderedLegs((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      orderedLegsRef.current = next;
      return next;
    });
  }, []);

  const handleDragEnd = useCallback(() => {
    if (!canReorder) return;
    const nextIds = orderedLegsRef.current.map((leg) => String(leg._id));
    persistSortOrder(nextIds);
  }, [canReorder, persistSortOrder]);

  async function persistLegIds(nextLegs) {
    try {
      await updateManifestV2({
        id: manifest._id,
        body: { legs: nextLegs.map((leg) => ({ _id: leg._id })) },
      }).unwrap();
      toast.success("Manifest legs updated");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to update manifest legs");
    }
  }

  async function handleRemove(legId) {
    const nextLegs = orderedLegs.filter((leg) => String(leg._id) !== String(legId));
    await persistLegIds(nextLegs);
  }

  async function handleAddSelected() {
    const existingIds = new Set(orderedLegs.map((leg) => String(leg._id)));
    const additions = pickerSelection
      .filter((id) => !existingIds.has(String(id)))
      .map((id) => ({ _id: id }));
    if (!additions.length) {
      setShowPicker(false);
      return;
    }
    await persistLegIds([...orderedLegs, ...additions]);
    setPickerSelection([]);
    setShowPicker(false);
  }

  const tableBody = (
    <tbody className="divide-y divide-gray-100">
      {orderedLegs.length === 0 ? (
        <tr>
          <td colSpan={8} className="px-4 py-6 text-center text-gray-500">
            No legs on this manifest.
          </td>
        </tr>
      ) : (
        orderedLegs.map((leg, index) => (
          <LegRow
            key={leg._id}
            leg={leg}
            index={index}
            canEdit={editable}
            canReorder={canReorder}
            moveRow={moveRow}
            onRemove={handleRemove}
          />
        ))
      )}
    </tbody>
  );

  const table = (
    <table className="min-w-full divide-y divide-gray-200 text-sm">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
          <th className="px-3 py-2 text-left font-medium text-gray-600">Load #</th>
          <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
          <th className="px-3 py-2 text-left font-medium text-gray-600">BOL #</th>
          <th className="px-3 py-2 text-left font-medium text-gray-600">Commodity</th>
          <th className="px-3 py-2 text-left font-medium text-gray-600">Hazmat</th>
          <th className="px-3 py-2 text-left font-medium text-gray-600">Appt</th>
          <th className="px-3 py-2 text-right font-medium text-gray-600">Actions</th>
        </tr>
      </thead>
      {tableBody}
    </table>
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <h2 className="text-lg font-semibold text-gray-900">Legs</h2>
        {editable ? (
          <Button
            type="alternative"
            size="sm"
            loading={isLoading}
            onClick={() => setShowPicker((prev) => !prev)}
          >
            {showPicker ? "Hide leg picker" : "Add legs"}
          </Button>
        ) : null}
      </div>

      {showPicker && editable ? (
        <div className="border-b border-gray-100 p-4">
          <ManifestV2LegPicker
            driverId={manifest.driver?._id || manifest.driver}
            parentCompany={parentCompany}
            startDate={manifest.startDate}
            endDate={manifest.endDate}
            selectedLegIds={pickerSelection}
            onChange={setPickerSelection}
          />
          <div className="mt-3 flex justify-end">
            <Button
              size="sm"
              loading={isLoading}
              disabled={!pickerSelection.length}
              onClick={handleAddSelected}
            >
              Add selected legs
            </Button>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto" onMouseUp={handleDragEnd} onTouchEnd={handleDragEnd}>
        {canReorder ? (
          <DndProvider backend={HTML5Backend}>{table}</DndProvider>
        ) : (
          table
        )}
      </div>
    </div>
  );
};

export default ManifestV2LegsTable;
